// Professional export: renders a clean, light-themed printable document (Save as PDF via print).
function esc(s) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Only http(s) links in exported documents; blocks javascript:/data: schemes.
function safeUrl(u) {
  return /^https?:\/\//i.test((u || "").trim()) ? esc(u.trim()) : "#";
}

const CSP_META = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src https: data:; script-src 'none'">`;

function openPrintable(html) {
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const w = window.open(url, "_blank");
  if (!w) {
    URL.revokeObjectURL(url);
    return false;
  }
  w.addEventListener("load", () => {
    w.focus();
    setTimeout(() => w.print(), 300);
    URL.revokeObjectURL(url);
  });
  return true;
}

const STYLE = `
  * { box-sizing: border-box; }
  body { font-family: Georgia, 'Times New Roman', serif; color: #14181f; background: #fff; margin: 0; padding: 56px 64px; line-height: 1.6; }
  .brand { font-family: Arial, Helvetica, sans-serif; letter-spacing: 0.14em; font-weight: 600; font-size: 12px; color: #0284a8; text-transform: uppercase; }
  h1 { font-size: 30px; font-weight: 600; margin: 8px 0 4px; line-height: 1.2; }
  .meta { font-family: Arial, sans-serif; font-size: 12px; color: #667085; margin-bottom: 28px; }
  h2 { font-family: Arial, sans-serif; font-size: 12px; letter-spacing: 0.16em; text-transform: uppercase; color: #0284a8; border-bottom: 1px solid #e4e7ec; padding-bottom: 6px; margin: 30px 0 12px; }
  p { margin: 0 0 12px; }
  .brief { font-size: 18px; color: #101828; }
  ul { margin: 0 0 12px; padding-left: 20px; }
  li { margin-bottom: 6px; }
  .persp { margin-bottom: 10px; }
  .persp .label { font-family: Arial, sans-serif; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: #667085; }
  .src { font-family: Arial, sans-serif; font-size: 13px; margin-bottom: 6px; }
  .src a { color: #0284a8; text-decoration: none; }
  .article p { font-size: 15px; }
  footer { margin-top: 40px; border-top: 1px solid #e4e7ec; padding-top: 14px; font-family: Arial, sans-serif; font-size: 11px; color: #98a2b3; }
  @media print { body { padding: 0; } @page { margin: 22mm; } }
`;

function insightBody(doc) {
  const facts = (doc.key_facts || []).map((f) => `<li>${esc(f)}</li>`).join("");
  const persp = (doc.consensus?.perspectives || [])
    .map((p) => `<div class="persp"><div class="label">${esc(p.label)}</div><div>${esc(p.view)}</div></div>`)
    .join("");
  const sources = (doc.sources || [])
    .map((s) => `<div class="src">${esc(s.name)} — <a href="${safeUrl(s.url)}">${esc(s.url)}</a></div>`)
    .join("");
  const analysis = (doc.analysis || "")
    .split(/\n{2,}|\n/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p>${esc(p).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</p>`).join("");
  return `
    <h2>In Brief</h2><p class="brief">${esc(doc.in_brief)}</p>
    <h2>The Analysis</h2>${analysis}
    <h2>Key Facts</h2><ul>${facts}</ul>
    <h2>Consensus &amp; Perspectives</h2><p><em>${esc(doc.consensus?.summary || "")}</em></p>${persp}
    <h2>Verified Sources</h2>${sources}
  `;
}

function readerBody(doc) {
  const concepts = (doc.concepts || [])
    .map((c) => `<li><strong>${esc(c.name)}</strong> — ${esc(c.summary)}</li>`).join("");
  const causal = (doc.causal_relations || [])
    .map((r) => `<li>${esc(r.cause)} → ${esc(r.effect)}</li>`).join("");
  const sources = (doc.sources || [])
    .map((s) => `<div class="src">${esc(s.name)} — <a href="${safeUrl(s.url)}">${esc(s.url)}</a></div>`).join("");
  const article = (doc.paragraphs || []).map((p) => `<p>${esc(p)}</p>`).join("");
  return `
    <h2>Insight Summary</h2><p class="brief">${esc(doc.insight_summary)}</p>
    <h2>Key Concepts</h2><ul>${concepts}</ul>
    ${causal ? `<h2>Causal Links</h2><ul>${causal}</ul>` : ""}
    <h2>Sources</h2>${sources}
    <h2>Full Text</h2><div class="article">${article}</div>
  `;
}

function crossrefBody(doc) {
  const agree = (doc.agreements || []).map((a) => `<li>${esc(a)}</li>`).join("");
  const frictions = (doc.frictions || [])
    .map((f) => {
      const pos = (f.positions || [])
        .map((p) => `<div class="persp"><div class="label">${esc(p.source)}</div><div>${esc(p.stance)}</div></div>`)
        .join("");
      return `<div style="margin-bottom:14px"><p><strong>${esc(f.point)}</strong></p>${pos}</div>`;
    })
    .join("");
  const sources = (doc.sources || [])
    .map((s) => `<div class="src">${esc(s.name)} — <a href="${safeUrl(s.url)}">${esc(s.url)}</a></div>`)
    .join("");
  return `
    <h2>Overview</h2><p class="brief">${esc(doc.summary)}</p>
    <h2>Points of Agreement</h2><ul>${agree}</ul>
    <h2>Friction Points</h2>${frictions}
    <h2>Forensic Verdict</h2><p>${esc(doc.verdict)}</p>
    <h2>Cross-Referenced Sources</h2>${sources}
  `;
}

export function exportDocument(doc, kind, opts = {}) {
  if (doc && doc.unified_output) {
    return exportUnifiedReport(doc, opts.lang === "it" ? "it" : "en");
  }
  const title = esc(doc.title || "Rexxpons Document");
  const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  let meta;
  if (kind === "reader") meta = `${esc(doc.site_name || "Source")} · ${doc.read_time_min || 1} min read · ${date}`;
  else if (kind === "crossref") meta = `Cross-Reference Report · ${(doc.sources || []).length} sources · ${date}`;
  else meta = `RexxSearch Insight · ${date}`;
  const body = kind === "reader" ? readerBody(doc) : kind === "crossref" ? crossrefBody(doc) : insightBody(doc);

  const html = `<!doctype html><html><head><meta charset="utf-8">${CSP_META}<title>${title}</title><style>${STYLE}</style></head>
  <body>
    <div class="brand">REXXPONS</div>
    <h1>${title}</h1>
    <div class="meta">${meta}</div>
    ${body}
    <footer>Generated by Rexxpons — the Cognitive Web Browser. An original project by Attorney Luigi Galano. © Luigi Galano. Verify critical decisions against primary sources.</footer>
  </body></html>`;

  return openPrintable(html);
}


// ---------------- Unified four-engine institutional report ----------------

const REPORT_L = {
  en: {
    subtitle: "Unified Cognitive Report", execSummary: "Executive Summary", systemsActivated: "Systems Activated",
    decision: "Decision Layer", recommendedPath: "Recommended Path", priorityActions: "Priority Actions",
    riskLevel: "Risk Level", legalUrgency: "Legal Urgency Index", finSustain: "Financial Sustainability Index",
    engineContributions: "Engine Contributions", rexxponsSynthesis: "Rexxpons — Synthesis",
    reasoning: "Rexxengine — Structured Reasoning", structuredKnowledge: "Structured Knowledge", comparisons: "Comparisons",
    rating: "RatingOnline — Financial Evaluation", score: "Indicative Score", financialRisks: "Financial Risks", balanceSheet: "Balance-Sheet Analysis",
    legal: "RexxLegal — Legal & Compliance", legalRisks: "Legal Risks", compliance: "Compliance Assessment", legalAdvice: "Strategic Legal Advice",
    unifiedPanel: "Unified Cognitive Panel", sources: "Verified Sources", insightLevel: "Insight Level", keyFacts: "Key Facts",
    growth: "Growth-Compass — Strategic Growth", projections: "Projections", industrialPlan: "Industrial Plan", growthStrategy: "Growth Strategy", investmentAnalysis: "Investment Analysis",
    confidential: "Confidential — for institutional use", prepared: "Prepared by Rexxpons — the Cognitive Web Browser",
    locale: "en-US",
  },
  it: {
    subtitle: "Rapporto Cognitivo Unificato", execSummary: "Sintesi Esecutiva", systemsActivated: "Sistemi Attivati",
    decision: "Livello Decisionale", recommendedPath: "Percorso Raccomandato", priorityActions: "Azioni Prioritarie",
    riskLevel: "Livello di Rischio", legalUrgency: "Indice di Urgenza Legale", finSustain: "Indice di Sostenibilità Finanziaria",
    engineContributions: "Contributi dei Motori", rexxponsSynthesis: "Rexxpons — Sintesi",
    reasoning: "Rexxengine — Ragionamento Strutturato", structuredKnowledge: "Conoscenza Strutturata", comparisons: "Confronti",
    rating: "RatingOnline — Valutazione Finanziaria", score: "Punteggio Indicativo", financialRisks: "Rischi Finanziari", balanceSheet: "Analisi di Bilancio",
    legal: "RexxLegal — Legale & Compliance", legalRisks: "Rischi Legali", compliance: "Valutazione di Conformità", legalAdvice: "Consulenza Legale Strategica",
    unifiedPanel: "Pannello Cognitivo Unificato", sources: "Fonti Verificate", insightLevel: "Livello di Insight", keyFacts: "Fatti Chiave",
    growth: "Growth-Compass — Crescita Strategica", projections: "Proiezioni", industrialPlan: "Piano Industriale", growthStrategy: "Strategia di Crescita", investmentAnalysis: "Analisi degli Investimenti",
    confidential: "Riservato — per uso istituzionale", prepared: "Preparato da Rexxpons — il Cognitive Web Browser",
    locale: "it-IT",
  },
};

const REPORT_STYLE = `
  * { box-sizing: border-box; }
  body { font-family: Georgia, 'Times New Roman', serif; color: #14181f; background: #fff; margin: 0; padding: 0; line-height: 1.6; }
  .page { padding: 56px 64px; }
  .brand { font-family: Arial, Helvetica, sans-serif; letter-spacing: 0.16em; font-weight: 700; font-size: 13px; color: #0284a8; text-transform: uppercase; }
  .cover { min-height: 92vh; display: flex; flex-direction: column; justify-content: center; page-break-after: always; }
  .cover h1 { font-size: 40px; font-weight: 600; margin: 18px 0 8px; line-height: 1.15; }
  .cover .subtitle { font-family: Arial, sans-serif; font-size: 15px; letter-spacing: 0.08em; text-transform: uppercase; color: #475467; }
  .cover .cmeta { font-family: Arial, sans-serif; font-size: 13px; color: #667085; margin-top: 26px; }
  .chips { margin-top: 18px; }
  .chip { display: inline-block; font-family: Arial, sans-serif; font-size: 11px; letter-spacing: 0.06em; border: 1px solid #cfd6dd; border-radius: 999px; padding: 4px 12px; margin: 0 6px 6px 0; color: #344054; }
  h2 { font-family: Arial, sans-serif; font-size: 12px; letter-spacing: 0.16em; text-transform: uppercase; color: #0284a8; border-bottom: 1px solid #e4e7ec; padding-bottom: 6px; margin: 34px 0 12px; }
  h3 { font-family: Arial, sans-serif; font-size: 13px; letter-spacing: 0.04em; color: #101828; margin: 20px 0 8px; }
  p { margin: 0 0 12px; }
  .brief { font-size: 18px; color: #101828; }
  ul, ol { margin: 0 0 12px; padding-left: 22px; }
  li { margin-bottom: 6px; }
  .kv { font-family: Arial, sans-serif; font-size: 13px; }
  .kv .label { color: #667085; text-transform: uppercase; letter-spacing: 0.08em; font-size: 10px; }
  .grid3 { display: flex; gap: 12px; margin: 12px 0; }
  .grid3 .box { flex: 1; border: 1px solid #e4e7ec; border-radius: 8px; padding: 10px 12px; }
  .item { margin-bottom: 8px; }
  .item .t { font-family: Arial, sans-serif; font-size: 13px; font-weight: 700; color: #101828; }
  .item .d { font-size: 14px; color: #344054; }
  .src { font-family: Arial, sans-serif; font-size: 13px; margin-bottom: 6px; }
  .src a { color: #0284a8; text-decoration: none; }
  .article p { font-size: 15px; }
  .section { page-break-inside: avoid; }
  footer { margin-top: 40px; border-top: 1px solid #e4e7ec; padding-top: 14px; font-family: Arial, sans-serif; font-size: 11px; color: #98a2b3; }
  @media print { @page { margin: 20mm; } }
`;

function md(text) {
  return (text || "").split(/\n{2,}|\n/).map((p) => p.trim()).filter(Boolean)
    .map((p) => `<p>${esc(p).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</p>`).join("");
}

function exportUnifiedReport(doc, lang) {
  const L = REPORT_L[lang];
  const uo = doc.unified_output || {};
  const title = esc(doc.title || "Rexxpons Report");
  const date = new Date().toLocaleDateString(L.locale, { year: "numeric", month: "long", day: "numeric" });
  const systems = ["Rexxpons", ...(uo.systems_activated || [])];
  const chips = systems.map((s) => `<span class="chip">${esc(s)}</span>`).join("");

  const fusion = uo.fusion_layer || {};
  const decision = uo.decision_layer || null;
  const eng = uo.rexxengine_reasoning || null;
  const rat = uo.ratingonline_evaluation || null;
  const leg = uo.rexxlegal_analysis || null;
  const gro = uo.growth_compass_analysis || null;

  const execSummary = `
    <div class="section">
      <h2>${L.execSummary}</h2>
      ${fusion.insight_level ? `<p class="kv"><span class="label">${L.insightLevel}:</span> <strong>${esc(fusion.insight_level)}</strong></p>` : ""}
      ${fusion.summary ? `<p class="brief">${esc(fusion.summary)}</p>` : ""}
      ${md(fusion.combined_interpretation)}
    </div>`;

  const decisionBlock = decision ? `
    <div class="section">
      <h2>${L.decision}</h2>
      ${decision.recommended_path ? `<h3>${L.recommendedPath}</h3><p>${esc(decision.recommended_path)}</p>` : ""}
      ${(decision.priority_actions || []).length ? `<h3>${L.priorityActions}</h3><ol>${decision.priority_actions.map((a) => `<li>${esc(a)}</li>`).join("")}</ol>` : ""}
      <div class="grid3">
        <div class="box kv"><div class="label">${L.riskLevel}</div><div><strong>${esc(decision.risk_level || "—")}</strong></div></div>
        <div class="box kv"><div class="label">${L.legalUrgency}</div><div><strong>${esc(decision.legal_urgency_index || "—")}</strong></div></div>
        <div class="box kv"><div class="label">${L.finSustain}</div><div><strong>${esc(decision.financial_sustainability_index || "—")}</strong></div></div>
      </div>
    </div>` : "";

  const rpBrief = doc.in_brief || doc.insight_summary || doc.summary || "";
  const rpBody = doc.analysis || (Array.isArray(doc.paragraphs) ? doc.paragraphs.join("\n\n") : "") || "";
  const rpFacts = (doc.key_facts && doc.key_facts.length ? doc.key_facts : doc.agreements) || [];
  const rexxponsSynthesis = `
    <div class="section">
      <h3>${L.rexxponsSynthesis}</h3>
      ${rpBrief ? `<p class="brief">${esc(rpBrief)}</p>` : ""}
      ${md(rpBody)}
      ${rpFacts.length ? `<h3>${L.keyFacts}</h3><ul>${rpFacts.map((f) => `<li>${esc(f)}</li>`).join("")}</ul>` : ""}
    </div>`;

  const reasoningBlock = eng ? `
    <div class="section">
      <h3>${L.reasoning}</h3>
      ${md(eng.explanation)}
      ${(eng.structured_knowledge || []).length ? `<h3>${L.structuredKnowledge}</h3>${eng.structured_knowledge.map((k) => `<div class="item"><span class="t">${esc(k.concept)}</span><div class="d">${esc(k.details)}</div></div>`).join("")}` : ""}
      ${(eng.comparisons || []).length ? `<h3>${L.comparisons}</h3>${eng.comparisons.map((c) => `<div class="item"><span class="t">${esc(c.item_a)} vs ${esc(c.item_b)}</span><div class="d">${esc(c.analysis)}</div></div>`).join("")}` : ""}
    </div>` : "";

  const ratingBlock = rat ? `
    <div class="section">
      <h3>${L.rating}</h3>
      ${rat.score ? `<p class="kv"><span class="label">${L.score}:</span> <strong>${esc(rat.score)}</strong></p>` : ""}
      ${(rat.financial_risks || []).length ? `<h3>${L.financialRisks}</h3>${rat.financial_risks.map((r) => `<div class="item"><span class="t">${esc(r.risk)}</span><div class="d">${esc(r.impact)}</div></div>`).join("")}` : ""}
      ${rat.balance_sheet_analysis ? `<h3>${L.balanceSheet}</h3>${md(rat.balance_sheet_analysis)}` : ""}
    </div>` : "";

  const legalBlock = leg ? `
    <div class="section">
      <h3>${L.legal}</h3>
      ${(leg.legal_risks || []).length ? `<h3>${L.legalRisks}</h3>${leg.legal_risks.map((r) => `<div class="item"><span class="t">${esc(r.issue)}</span><div class="d">${esc(r.analysis)}</div></div>`).join("")}` : ""}
      ${leg.compliance_assessment ? `<h3>${L.compliance}</h3>${md(leg.compliance_assessment)}` : ""}
      ${leg.strategic_legal_advice ? `<h3>${L.legalAdvice}</h3>${md(leg.strategic_legal_advice)}` : ""}
    </div>` : "";

  const growthBlock = gro ? `
    <div class="section">
      <h3>${L.growth}</h3>
      ${(gro.investment_analysis || []).length ? `<h3>${L.investmentAnalysis}</h3><ul>${gro.investment_analysis.map((m) => `<li><strong>${esc(m.metric)}:</strong> ${esc(m.value)}</li>`).join("")}</ul>` : ""}
      ${gro.growth_strategy ? `<h3>${L.growthStrategy}</h3>${md(gro.growth_strategy)}` : ""}
      ${gro.projections ? `<h3>${L.projections}</h3>${md(gro.projections)}` : ""}
      ${gro.industrial_plan ? `<h3>${L.industrialPlan}</h3>${md(gro.industrial_plan)}` : ""}
    </div>` : "";

  const sources = (doc.sources || []).map((s) => `<div class="src">${esc(s.name)} — <a href="${safeUrl(s.url)}">${esc(s.url)}</a></div>`).join("");
  const html = `<!doctype html><html lang="${lang}"><head><meta charset="utf-8">${CSP_META}<title>${title}</title><style>${REPORT_STYLE}</style></head>
  <body>
    <div class="page cover">
      <div class="brand">REXXPONS</div>
      <div class="subtitle">${L.subtitle}</div>
      <h1>${title}</h1>
      <div class="chips">${chips}</div>
      <div class="cmeta">${date} · ${L.confidential}</div>
    </div>
    <div class="page">
      ${execSummary}
      ${decisionBlock}
      <h2>${L.systemsActivated}</h2>${uo.activation_note ? `<p>${esc(uo.activation_note)}</p>` : ""}
      <h2>${L.engineContributions}</h2>
      ${rexxponsSynthesis}
      ${reasoningBlock}
      ${ratingBlock}
      ${legalBlock}
      ${growthBlock}
      <h2>${L.sources}</h2>${sources}
      <footer>${L.prepared}. © Luigi Galano · Galano &amp; Partners. Verify critical decisions against primary sources.</footer>
    </div>
  </body></html>`;

  return openPrintable(html);
}
